import authApi from './authApi';
import { EXTENSION_ACTIONS } from './constants';
import {
  CONV_STARTER_ACTION,
  ConvStarterActionTypes,
  IExtensionMessage,
  StarterData,
} from './types/extension';

export const chromeApi = typeof chrome !== 'undefined' ? chrome : undefined;

// Foam Extension Utils (Main Extension)
export const foamExtensionUtils = {
  /**
   * Sends ping; returns true only if extension responds.
   */
  isExtensionAvailable: (): Promise<boolean> =>
    new Promise((resolve) => {
      const extensionId = process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID;
      if (
        !extensionId ||
        typeof chromeApi?.runtime?.sendMessage !== 'function'
      ) {
        resolve(false);
        return;
      }
      chromeApi!.runtime!.sendMessage(
        extensionId,
        { action: EXTENSION_ACTIONS.ping },
        (response: unknown) => {
          resolve((response as { status?: string })?.status === 'success');
        },
      );
    }),

  sendRefreshWatchlistToChromeExtension: (): void => {
    const extensionId = process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID;
    if (!extensionId || typeof chromeApi?.runtime?.sendMessage !== 'function') {
      return;
    }
    chromeApi.runtime.sendMessage(
      extensionId,
      { action: EXTENSION_ACTIONS.refreshWatchlist },
      () => {
        // Missing extension, MV3 worker asleep, or no `onMessage` handler → lastError is set; do not treat as fatal.
        void chromeApi?.runtime?.lastError;
      },
    );
  },

  sendTokenToChromeExtension: () => {
    chromeApi?.runtime?.sendMessage(
      process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID ?? '',
      {
        action: EXTENSION_ACTIONS.login,
        jwt: authApi.jwtAccessToken,
      },
    );
    foamAssistUtils.sendTokens();
  },

  sendLogoutToChromeExtension: () => {
    if (typeof chromeApi?.runtime?.sendMessage !== 'function') {
      return;
    }
    foamAssistUtils.sendLogout();
    return chromeApi?.runtime?.sendMessage(
      process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID ?? '',
      {
        action: EXTENSION_ACTIONS.logout,
      },
    );
  },

  sendRedirectRequestToChromeExtension:
    async (): Promise<IExtensionMessage | void> => {
      return new Promise<IExtensionMessage | void>(
        (resolve) =>
          chromeApi?.runtime?.sendMessage(
            process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID ?? '',
            {
              action: EXTENSION_ACTIONS.redirectReady,
            },
            resolve,
          ),
      );
    },

  sendToggleIframeToChromeExtension: () => {
    foamAssistUtils.sendOpenSidepanel();
    return chromeApi?.runtime?.sendMessage(
      process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID ?? '',
      {
        action: EXTENSION_ACTIONS.toggleIframe,
      },
    );
  },

  sendClickTryGmail: () => {
    foamAssistUtils.sendClickTryGmail();

    return chromeApi?.runtime?.sendMessage(
      process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID ?? '',
      {
        action: EXTENSION_ACTIONS.clickGmail,
      },
    );
  },
  sendOpenOnFoamAssist: (shouldHideWelcome: boolean = false) => {
    // Check if Chrome runtime is available
    if (!chromeApi?.runtime?.sendMessage) {
      throw new Error(
        'Chrome extension runtime not available. Please check if the extension is enabled.',
      );
    }

    chromeApi.runtime.sendMessage(
      process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID ?? '',
      {
        action: EXTENSION_ACTIONS.openOnFoamAssist,
        data: { shouldHideWelcome },
      },
    );
  },

  sendConversationStarterAction<StarterType extends ConvStarterActionTypes>(
    type: StarterType,
    data: StarterData<StarterType>,
  ) {
    // Check if Chrome runtime is available
    if (!chromeApi?.runtime?.sendMessage) {
      throw new Error(
        'Chrome extension runtime not available. Please check if the extension is enabled.',
      );
    }

    chromeApi.runtime.sendMessage(
      process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID ?? '',
      {
        action: CONV_STARTER_ACTION,
        type,
        data,
      },
    );
  },
};

// Foam Assist Utils (Kraken Extension)
/**
 * @deprecated Use foamExtensionUtils instead
 */
export const foamAssistUtils = {
  // Message action constants
  LOGIN_SUCCESS_MESSAGE: 'foam-assist-login-success',

  /**
   * @deprecated Use foamExtensionUtils instead
   */
  sendTokens: () => {
    chromeApi?.runtime?.sendMessage(
      process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID ?? '',
      {
        action: EXTENSION_ACTIONS.login,
        jwt: authApi.jwtAccessToken,
      },
    );
  },

  /**
   * @deprecated Use foamExtensionUtils instead
   */
  sendLogout: () => {
    if (typeof chromeApi?.runtime?.sendMessage !== 'function') {
      return;
    }
    chromeApi?.runtime?.sendMessage(
      process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID ?? '',
      {
        action: EXTENSION_ACTIONS.logout,
      },
    );
  },

  /**
   * @deprecated Use foamExtensionUtils instead
   */
  sendOpenSidepanel: () => {
    return chromeApi?.runtime?.sendMessage(
      process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID ?? '',
      {
        action: EXTENSION_ACTIONS.toggleIframe,
      },
    );
  },

  /**
   * @deprecated Use foamExtensionUtils instead
   */
  sendClickTryGmail: () => {
    return chromeApi?.runtime?.sendMessage(
      process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID ?? '',
      {
        action: EXTENSION_ACTIONS.clickGmail,
      },
    );
  },
};
